import React, { useState, useEffect } from 'react';
import { Table, Dropdown, Menu, Button, Space, Typography, Modal, message, Descriptions } from 'antd';
import { EyeOutlined, FlagOutlined, CheckOutlined, DownOutlined, DeleteOutlined } from '@ant-design/icons';
import './ViewListProvider.css';

const { Title } = Typography;

const ViewListProvider = () => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  // Fetch admins from backend
  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await fetch('/backend/superadmin_get_admins.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Erreur de parsing JSON:', e, 'Texte reçu:', text);
        throw new Error('Réponse non-JSON');
      }
      if (response.ok) {
        console.log('Données admins reçues:', data.admins);
        setDataSource(data.admins || []);
      } else {
        if (response.status === 403) {
          message.error('Session expirée. Veuillez vous reconnecter.');
        } else {
          message.error(data.error || `Erreur HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Erreur de réseau:', error.message);
      message.error('Erreur réseau lors de la récupération des administrateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  // Handle view
  const handleView = (record) => {
    console.log('View:', record);
    setSelectedAdmin(record);
    setIsViewModalVisible(true);
  };

  // Handle flag
  const handleFlag = async (record) => {
    console.log('Flag:', record);
    try {
      const response = await fetch('/backend/superadmin_flag_admin.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: record.id }),
      });
      const data = await response.json();
      console.log('Réponse flag:', data);
      if (response.ok) {
        message.success(data.message || 'Administrateur signalé avec succès');
        fetchAdmins();
      } else {
        message.error(data.error || `Erreur HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur réseau lors du signalement:', error);
      message.error('Erreur réseau lors du signalement');
    }
  };

  // Handle approve
  const handleApprove = async (record) => {
    console.log('Approve:', record);
    try {
      const response = await fetch('/backend/superadmin_approve_admin.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: record.id }),
      });
      const data = await response.json();
      console.log('Réponse approve:', data);
      if (response.ok) {
        message.success(data.message || 'Administrateur approuvé avec succès');
        fetchAdmins();
      } else {
        message.error(data.error || `Erreur HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur réseau lors de l\'approbation:', error);
      message.error('Erreur réseau lors de l\'approbation');
    }
  };

  // Handle delete
  const handleDelete = (record) => {
    console.log('handleDelete appelé pour:', record);
    setAdminToDelete(record);
    setIsDeleteModalVisible(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!adminToDelete) return;
    console.log('Confirmation de suppression pour ID:', adminToDelete.id);
    try {
      const response = await fetch('/backend/superadmin_delete_admin.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: adminToDelete.id }),
      });
      console.log('Réponse de suppression:', response.status, response.statusText);
      const data = await response.json();
      console.log('Données de réponse:', data);
      if (response.ok) {
        message.success(data.message || 'Administrateur supprimé avec succès');
        setIsDeleteModalVisible(false);
        setAdminToDelete(null);
        fetchAdmins();
      } else {
        message.error(data.error || `Erreur HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur réseau lors de la suppression:', error);
      message.error('Erreur réseau lors de la suppression');
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    console.log('Suppression annulée');
    setIsDeleteModalVisible(false);
    setAdminToDelete(null);
  };

  // Handle view modal close
  const handleViewModalClose = () => {
    setIsViewModalVisible(false);
    setSelectedAdmin(null);
  };

  // Menu for dropdown actions
  const menu = (record) => (
    <Menu>
      <Menu.Item key="1" icon={<EyeOutlined />} onClick={() => handleView(record)}>
        View
      </Menu.Item>
      <Menu.Item key="2" icon={<FlagOutlined />} onClick={() => handleFlag(record)}>
        Flag
      </Menu.Item>
      <Menu.Item key="3" icon={<CheckOutlined />} onClick={() => handleApprove(record)}>
        Approve
      </Menu.Item>
      <Menu.Item key="4" icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
        Delete
      </Menu.Item>
    </Menu>
  );

  // Define columns
  const columns = [
    { title: 'Name', dataIndex: 'full_names', key: 'full_names' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Category', dataIndex: 'category', key: 'category' },
    { title: 'ZIP Code', dataIndex: 'zip_code', key: 'zip_code' },
    { title: 'State', dataIndex: 'state', key: 'state' },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status', 
      render: (status) => (
        <span className={status === 'Active' ? 'status-active' : status === 'Flagged' ? 'status-flagged' : 'status-pending'}>
          {status || 'Pending Approval'}
        </span>
      ),
    },
    { 
      title: 'Action', 
      key: 'action', 
      render: (_, record) => (
        <Space size="middle">
          <Dropdown overlay={menu(record)} trigger={['click']}>
            <Button icon={<DownOutlined />} size="small">
              Actions
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div className="view-list-provider-container">
      <Title level={3} className="view-list-provider-title">Administrators</Title>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={{ pageSize: 5 }}
        className="view-list-provider-table"
        loading={loading}
      />
      {/* View Modal */}
      <Modal
        title={`Détails de l'administrateur - ${selectedAdmin?.full_names}`}
        open={isViewModalVisible}
        onOk={handleViewModalClose}
        onCancel={handleViewModalClose}
        okText="Fermer"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={600}
      >
        {selectedAdmin && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">{selectedAdmin.id}</Descriptions.Item>
            <Descriptions.Item label="Nom complet">{selectedAdmin.full_names || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedAdmin.email}</Descriptions.Item>
            <Descriptions.Item label="Catégorie">{selectedAdmin.category || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Code postal">{selectedAdmin.zip_code || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="État">{selectedAdmin.state || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Statut">{selectedAdmin.status || 'Pending Approval'}</Descriptions.Item>
            <Descriptions.Item label="Créé le">{selectedAdmin.created_at}</Descriptions.Item>
            <Descriptions.Item label="Équipements">{selectedAdmin.amenities || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Espace disponible">{selectedAdmin.available_space || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Lits">{selectedAdmin.beds || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Localisation">{selectedAdmin.location || 'N/A'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirmer la suppression"
        open={isDeleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Supprimer"
        okType="danger"
        cancelText="Annuler"
      >
        <p>
          Voulez-vous vraiment supprimer l'administrateur {adminToDelete?.full_names} ({adminToDelete?.email}) et toutes ses informations ?
        </p>
      </Modal>
    </div>
  );
};

export default ViewListProvider;